import type { ContentItem, ContentItemId } from '@kidshub/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { formatLastSeen } from '../lib/relative-time.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { Button, Card, EmptyState, Segmented } from '../ui/kit.js';
import { DailyChart, HourHeat } from './charts.js';
import {
  dayLabel,
  feltFor,
  ItemThumb,
  PeriodSwitch,
  useDurationText,
  useWatchingScope,
} from './watching-scope.js';
import './watching.css';

/**
 * What a child watched, for the parent who chose it.
 *
 * The questions a parent actually asks, in the order they ask them: how much,
 * when, what she went back to, what she ignores, what she abandons. Decided on
 * the board `.docs/design/watch-analytics` (2026-10-09): its own place in the
 * navigation, a week by default, and an offer to close what is never opened.
 *
 * How a viewing ended needs the tablet to report its viewings, which only
 * builds from that date do. Until then that card says so, rather than drawing
 * an empty chart a parent would read as "nothing ended".
 */

/** The hours the heat map shows unless watching happened outside them. */
const WAKING_HOURS = { first: 7, last: 20 };

export const WatchingPage = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const duration = useDurationText();

  const children = useResource('children', () => apiClient.children.list());
  const content = useResource('content', () => apiClient.catalog.list());
  const collections = useResource('collections', () => apiClient.collections.list());

  const familyChildren =
    children.resource.status === 'ready' ? children.resource.data.children : [];
  const scope = useWatchingScope(familyChildren);
  const childId = scope.child?.child.id ?? null;

  const overview = useResource(`watching:${childId}:${scope.days}`, () =>
    childId === null
      ? Promise.resolve(null)
      : apiClient.analytics.overview({ childId, days: scope.days }),
  );
  const [closingId, setClosingId] = useState<ContentItemId | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const items = new Map(
    (content.resource.status === 'ready' ? content.resource.data.items : []).map((item) => [
      item.id,
      item,
    ]),
  );
  const allCollections =
    collections.resource.status === 'ready' ? collections.resource.data.collections : [];
  const homeOf = (itemId: string) =>
    allCollections.find((collection) => collection.itemIds.includes(itemId as ContentItemId))
      ?.title ?? t('library.card.noCollection');

  const data = overview.resource.status === 'ready' ? overview.resource.data : null;
  const name = scope.child?.child.name ?? '';

  const close = async (contentItemId: ContentItemId) => {
    if (childId === null) {
      return;
    }
    setErrorKey(null);
    setClosingId(contentItemId);
    try {
      await apiClient.catalog.setApproval({ contentItemId, childId, isApproved: false });
      overview.reload();
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setClosingId(null);
    }
  };

  if (children.resource.status === 'ready' && familyChildren.length === 0) {
    return (
      <AppShell title={t('watching.title')}>
        <EmptyState title={t('watching.noChildren')} />
      </AppShell>
    );
  }

  const today = data === null ? '' : (data.days[data.days.length - 1]?.day ?? '');
  const isLong = scope.days > 7;
  const rowFor = (item: ContentItem | undefined, id: string) => ({
    title: item?.title ?? t('watching.removedItem'),
    felt: feltFor(id, allCollections),
  });

  return (
    <AppShell
      title={t('watching.title')}
      subtitle={t('watching.subtitle', { name, period: t(`watching.periodText.${scope.days}`) })}
      actions={<PeriodSwitch days={scope.days} onChange={scope.setDays} />}
    >
      {familyChildren.length > 1 && scope.child !== null ? (
        <div className="wa-scope">
          <Segmented
            label={t('watching.child')}
            value={scope.child.child.id}
            options={familyChildren.map((entry) => ({
              value: entry.child.id,
              label: entry.child.name,
            }))}
            onChange={scope.setChild}
          />
        </div>
      ) : null}

      {overview.resource.status === 'failed' || errorKey !== null ? (
        <p className="help err" role="alert">
          {t(errorKey ?? toErrorKey(overview.resource.error))}
        </p>
      ) : null}

      {data === null ? (
        <p className="t-meta">{t('common.loading')}</p>
      ) : (
        <>
          <div className="wa-kpis">
            <Kpi
              value={duration(data.totals.watchedSeconds)}
              label={t('watching.kpi.watched')}
              detail={
                data.days.length > 1
                  ? t('watching.kpi.perDay', {
                      duration: duration(data.totals.watchedSeconds / data.days.length),
                    })
                  : t('watching.kpi.limit', { duration: duration(data.dailyLimitSeconds) })
              }
            />
            <Kpi
              value={String(data.totals.opens)}
              label={t('watching.kpi.opens')}
              detail={t('watching.kpi.distinct', { count: data.totals.distinctItems })}
            />
            <Kpi
              value={
                data.totals.completionRate === null
                  ? '—'
                  : `%${Math.round(data.totals.completionRate * 100)}`
              }
              label={t('watching.kpi.completed')}
              detail={
                data.totals.completionRate === null
                  ? t('watching.pendingShort')
                  : t('watching.kpi.completedDetail')
              }
            />
            <Kpi
              value={String(data.totals.quickQuits)}
              label={t('watching.kpi.quickQuits')}
              detail={t('watching.kpi.quickQuitsDetail')}
            />
          </div>

          <div className="wa-grid two">
            <div className="wa-grid">
              <Card
                title={t('watching.daily.title')}
                meta={data.days.length > 1 ? t('watching.daily.meta') : t('watching.daily.today')}
              >
                <DailyChart
                  label={t('watching.daily.title')}
                  limit={Math.round(data.dailyLimitSeconds / 60)}
                  limitLabel={t('watching.daily.limit', {
                    minutes: Math.round(data.dailyLimitSeconds / 60),
                  })}
                  bars={data.days.map((day) => {
                    const label =
                      day.day === today
                        ? t('watching.today')
                        : dayLabel(day.day, today, locale, isLong);
                    return {
                      label,
                      value: Math.round(day.watchedSeconds / 60),
                      tip: (
                        <>
                          <b>{label}</b>
                          {t('watching.daily.tip', {
                            duration: duration(day.watchedSeconds),
                            count: day.opens,
                          })}
                        </>
                      ),
                    };
                  })}
                />
              </Card>

              <Card title={t('watching.top.title')} meta={t('watching.top.meta')}>
                {data.top.length === 0 ? (
                  <p className="t-meta">{t('watching.top.empty')}</p>
                ) : (
                  <ol className="wa-list">
                    {data.top.map((stat, index) => {
                      const item = items.get(stat.contentItemId);
                      const row = rowFor(item, stat.contentItemId);
                      return (
                        <li key={stat.contentItemId} className="wa-row">
                          <span className="wa-rank num">{index + 1}</span>
                          <ItemThumb item={item} felt={row.felt} />
                          <div className="wa-t">
                            <Link to={`/watching/items/${stat.contentItemId}${scope.search}`}>
                              {row.title}
                            </Link>
                            <span>
                              {t('watching.top.line', {
                                collection: homeOf(stat.contentItemId),
                                count: stat.opens,
                                duration: duration(stat.watchedSeconds),
                              })}
                            </span>
                          </div>
                          {stat.averageCompletion === null ? (
                            <span />
                          ) : (
                            <div className="wa-num" title={t('watching.top.completionHint')}>
                              <b>%{Math.round(stat.averageCompletion * 100)}</b>
                              <span className="wa-meter">
                                <i
                                  style={{ width: `${Math.round(stat.averageCompletion * 100)}%` }}
                                />
                              </span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </Card>

              <Card title={t('watching.hours.title')} meta={t('watching.hours.meta')}>
                <HoursCard
                  days={data.days.map((day) => day.day).slice(-7)}
                  hours={data.hours}
                  label={(day) =>
                    day === today ? t('watching.today') : dayLabel(day, today, locale, false)
                  }
                  title={(row, hour, seconds) =>
                    t('watching.hours.cell', {
                      day: row,
                      hour: String(hour).padStart(2, '0'),
                      duration: duration(seconds),
                    })
                  }
                />
                <div className="wa-scale">
                  <span>{t('watching.hours.less')}</span>
                  <i className="wa-h1" />
                  <i className="wa-h2" />
                  <i className="wa-h3" />
                  <i className="wa-h4" />
                  <span>{t('watching.hours.more')}</span>
                </div>
              </Card>
            </div>

            <div className="wa-grid">
              <Card title={t('watching.never.title')} meta={t('watching.never.meta', { name })}>
                {data.neverWatched.length === 0 ? (
                  <p className="t-meta">{t('watching.never.empty')}</p>
                ) : (
                  <ul className="wa-list">
                    {data.neverWatched.map((entry) => {
                      const item = items.get(entry.contentItemId);
                      const row = rowFor(item, entry.contentItemId);
                      return (
                        <li key={entry.contentItemId} className="wa-row no-rank">
                          <ItemThumb item={item} felt={row.felt} />
                          <div className="wa-t">
                            <b>{row.title}</b>
                            <span>
                              {t('watching.never.line', {
                                collection: homeOf(entry.contentItemId),
                                when: formatLastSeen(entry.approvedAt, locale),
                              })}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            small
                            disabled={closingId === entry.contentItemId}
                            aria-label={t('watching.never.closeFor', { title: row.title, name })}
                            onClick={() => void close(entry.contentItemId)}
                          >
                            {t('watching.never.close')}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>

              <Card title={t('watching.quick.title')} meta={t('watching.quick.meta')}>
                {data.quickQuits.length === 0 ? (
                  <p className="t-meta">{t('watching.quick.empty')}</p>
                ) : (
                  <ul className="wa-list">
                    {data.quickQuits.map((entry) => {
                      const item = items.get(entry.contentItemId);
                      const row = rowFor(item, entry.contentItemId);
                      return (
                        <li key={entry.contentItemId} className="wa-row no-rank">
                          <ItemThumb item={item} felt={row.felt} />
                          <div className="wa-t">
                            <Link to={`/watching/items/${entry.contentItemId}${scope.search}`}>
                              {row.title}
                            </Link>
                            <span>
                              {t('watching.quick.line', {
                                opens: entry.opens,
                                quick: entry.quickQuits,
                              })}
                            </span>
                          </div>
                          <span />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>

              <Card title={t('watching.endings.title')} meta={t('watching.endings.meta')}>
                {data.endings.length === 0 ? (
                  <p className="wa-pending">
                    {data.hasViews ? t('watching.endings.empty') : t('watching.pending')}
                  </p>
                ) : (
                  <div className="wa-bars">
                    {data.endings.map((ending) => {
                      const top = Math.max(...data.endings.map((entry) => entry.count));
                      return (
                        <div key={ending.kind} className="wa-bar-row">
                          <span>{t(`watching.ending.${ending.kind}`)}</span>
                          <span className="wa-bar-track">
                            <i style={{ width: `${(ending.count / top) * 100}%` }} />
                          </span>
                          <b>{ending.count}</b>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
};

const Kpi = ({ value, label, detail }: { value: string; label: string; detail: string }) => (
  <div className="wa-kpi">
    <b>{value}</b>
    <span>{label}</span>
    <small>{detail}</small>
  </div>
);

/** The heat map's rows: one per day of the last week, hours widened to fit what happened. */
const HoursCard = ({
  days,
  hours,
  label,
  title,
}: {
  days: string[];
  hours: { day: string; hour: number; watchedSeconds: number }[];
  label: (day: string) => string;
  title: (row: string, hour: number, seconds: number) => string;
}) => {
  const first = Math.min(WAKING_HOURS.first, ...hours.map((slot) => slot.hour));
  const last = Math.max(WAKING_HOURS.last, ...hours.map((slot) => slot.hour));
  const rows = days.map((day) => ({
    label: label(day),
    seconds: Array.from(
      { length: last - first + 1 },
      (_, index) =>
        hours.find((slot) => slot.day === day && slot.hour === first + index)?.watchedSeconds ?? 0,
    ),
  }));
  return <HourHeat rows={rows} firstHour={first} lastHour={last} cellTitle={title} />;
};
